export class Property {
  constructor(data = {}) {
    // Basic Information
    this.id = data.id || "";
    this.userName = data.userName || "";
    this.userEmail = data.userEmail || "";
    this.userContact = data.userContact || "";
    this.adminName = data.adminName || "";

    // Admin Approval Status
    this.status = data.status || "pending"; // pending, approved, rejected, draft
    this.statusReason = data.statusReason || ""; // Reason for rejection or notes
    this.approvedBy = data.approvedBy || ""; // Admin who approved/rejected
    this.approvedAt = data.approvedAt ? new Date(data.approvedAt) : null;

    // Address Details
    this.country = data.country || "";
    this.city = data.city || "";
    this.area = data.area || "";
    this.street = data.street || "";
    this.googleMap = data.googleMap || ""; // Google Maps URL or coordinates
    this.landmark = data.landmark || "";

    // Property Specifications
    this.quantity = data.quantity || 0;
    this.listingType = data.listingType || "for_sale"; // for_sale, for_rent
    this.propertyType = data.propertyType || ""; // Apartment, Villa, Commercial, etc.
    this.furnishing = data.furnishing || ""; // Furnished, Semi-Furnished, Unfurnished
    this.propertyStatus = data.propertyStatus || ""; // Ready, Offplan, Distress

    // NEW: Market Status
    this.marketStatus = data.marketStatus || "open"; // sold, open, in-progress

    // Size Details
    this.builtUpArea = data.builtUpArea || 0; // BUA in sq.ft or sq.m
    this.plotSize = data.plotSize || 0; // Plot size in sq.ft or sq.m
    this.areaUnit = data.areaUnit || "sq.ft"; // sq.ft, sq.m, acres, etc.

    // Room Details (Optional)
    this.bedrooms = data.bedrooms || 0;
    this.bathrooms = data.bathrooms || 0;
    this.livingRooms = data.livingRooms || 0;
    this.kitchens = data.kitchens || 0;
    this.parkingSpaces = data.parkingSpaces || 0;

    // Listing Information
    this.listedBy = data.listedBy || ""; // Owner, Agent, Developer, etc.
    this.ownershipStatus = data.ownershipStatus || ""; // Single, Joint, Company
    this.mortgageStatus = data.mortgageStatus || ""; // Mortgaged, Fully Paid, Partially Paid
    this.exclusivity = data.exclusivity || false; // Is this property listed anywhere else?

    // Pricing Information
    this.listPrice = data.listPrice || 0;
    this.finalPrice = data.finalPrice || 0;
    this.currency = data.currency || "AED";
    this.priceNegotiable = data.priceNegotiable || false;

    // Marketing Preferences
    this.marketingPreference = data.marketingPreference || "listOnly"; // listOnly, listAndPromote
    this.webListingDetails = data.webListingDetails || {
      showPrice: true,
      showBasicDetails: true,
      showPictures: true,
      showLocation: true,
    };

    // Additional Instructions
    this.listingInstructions = data.listingInstructions || "";
    this.specialFeatures = data.specialFeatures || [];
    this.otherFeatures = data.otherFeatures || "";

    // Media
    this.imageUrls = data.imageUrls || [];
    this.videoUrl = data.videoUrl || "";
    this.floorPlans = data.floorPlans || [];

    // Description
    this.description = data.description || "";
    this.title = this.title || "";

    // Timestamps
    this.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    this.availableFrom = data.availableFrom
      ? new Date(data.availableFrom)
      : null;

    // View Tracking
    this.views = data.views || 0;
    this.viewHistory = data.viewHistory || []; // Optional: detailed view tracking
    this.lastViewedAt = data.lastViewedAt ? new Date(data.lastViewedAt) : null;
  }

  static fromJson(json) {
    return new Property(json);
  }

  toJson() {
    return {
      // Basic Information
      id: this.id,
      userName: this.userName,
      userEmail: this.userEmail,
      userContact: this.userContact,
      adminName: this.adminName,

      // Admin Approval Status
      status: this.status,
      statusReason: this.statusReason,
      approvedBy: this.approvedBy,
      approvedAt: this.approvedAt ? this.approvedAt.toISOString() : null,

      // Address Details
      country: this.country,
      city: this.city,
      area: this.area,
      street: this.street,
      googleMap: this.googleMap,
      landmark: this.landmark,

      // Property Specifications
      quantity: this.quantity,
      listingType: this.listingType,
      propertyType: this.propertyType,
      furnishing: this.furnishing,
      propertyStatus: this.propertyStatus,

      // NEW: Market Status
      marketStatus: this.marketStatus,

      // Size Details
      builtUpArea: this.builtUpArea,
      plotSize: this.plotSize,
      areaUnit: this.areaUnit,

      // Room Details
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      livingRooms: this.livingRooms,
      kitchens: this.kitchens,
      parkingSpaces: this.parkingSpaces,

      // Listing Information
      listedBy: this.listedBy,
      ownershipStatus: this.ownershipStatus,
      mortgageStatus: this.mortgageStatus,
      exclusivity: this.exclusivity,

      // Pricing Information
      listPrice: this.listPrice,
      finalPrice: this.finalPrice,
      currency: this.currency,
      priceNegotiable: this.priceNegotiable,

      // Marketing Preferences
      marketingPreference: this.marketingPreference,
      webListingDetails: this.webListingDetails,

      // Additional Information
      listingInstructions: this.listingInstructions,
      specialFeatures: this.specialFeatures,
      otherFeatures: this.otherFeatures,

      // Media
      imageUrls: this.imageUrls,
      videoUrl: this.videoUrl,
      floorPlans: this.floorPlans,

      // Description
      description: this.description,
      title: this.title,

      // Timestamps
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
      availableFrom: this.availableFrom
        ? this.availableFrom.toISOString()
        : null,

      // View Tracking
      views: this.views,
      viewHistory: this.viewHistory,
      lastViewedAt: this.lastViewedAt ? this.lastViewedAt.toISOString() : null,
    };
  }

  // Helper methods
  getFullAddress() {
    return `${this.city}`.trim();
  }

  getDisplayPrice() {
    const price = this.finalPrice !== 0 ? this.finalPrice : this.listPrice;
    return `AED ${price}`;
  }

  getPropertySize() {
    return `${this.builtUpArea} ${this.areaUnit}`;
  }

  hasImages() {
    return this.imageUrls && this.imageUrls.length > 0;
  }

  getPrimaryImage() {
    return this.hasImages() ? this.imageUrls[0] : null;
  }

  // Status related methods
  isPending() {
    return this.status === "pending";
  }

  isApproved() {
    return this.status === "approved";
  }

  isRejected() {
    return this.status === "rejected";
  }

  isDraft() {
    return this.status === "draft";
  }

  // Listing Type Helpers
  isForRent() {
    return this.listingType === "for_rent";
  }

  isForSale() {
    return this.listingType === "for_sale";
  }

  getListingTypeText() {
    switch (this.listingType) {
      case "for_rent":
        return "For Rent";
      case "for_sale":
        return "For Sale";
      default:
        return this.listingType;
    }
  }

  // NEW: Market Status Helpers
  isSold() {
    return this.marketStatus === "sold";
  }

  isOpen() {
    return this.marketStatus === "open";
  }

  isInProgress() {
    return this.marketStatus === "in-progress";
  }

  getMarketStatusText() {
    switch (this.marketStatus) {
      case "sold":
        return "Sold";
      case "open":
        return "Available";
      case "in-progress":
        return "In Progress";
      default:
        return this.marketStatus;
    }
  }

  getMarketStatusColor() {
    switch (this.marketStatus) {
      case "sold":
        return "success";
      case "open":
        return "info";
      case "in-progress":
        return "warning";
      default:
        return "default";
    }
  }

  getStatusColor() {
    switch (this.status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      case "draft":
        return "default";
      default:
        return "default";
    }
  }

  getStatusText() {
    switch (this.status) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "pending":
        return "Pending Review";
      case "draft":
        return "Draft";
      default:
        return this.status;
    }
  }

  // Approval methods
  approve(adminName, reason = "") {
    this.status = "approved";
    this.approvedBy = adminName;
    this.approvedAt = new Date();
    this.statusReason = reason;
    this.updatedAt = new Date();
  }

  reject(adminName, reason = "") {
    this.status = "rejected";
    this.approvedBy = adminName;
    this.approvedAt = new Date();
    this.statusReason = reason;
    this.updatedAt = new Date();
  }

  setPending() {
    this.status = "pending";
    this.statusReason = "";
    this.updatedAt = new Date();
  }

  setDraft() {
    this.status = "draft";
    this.updatedAt = new Date();
  }

  // NEW: Market Status methods
  markAsSold() {
    this.marketStatus = "sold";
    this.updatedAt = new Date();
  }

  markAsOpen() {
    this.marketStatus = "open";
    this.updatedAt = new Date();
  }

  markAsInProgress() {
    this.marketStatus = "in-progress";
    this.updatedAt = new Date();
  }

  // Validation method
  isValid() {
    return (
      this.userName &&
      this.userContact &&
      this.country &&
      this.city &&
      this.listingType &&
      this.propertyType &&
      this.listPrice > 0
    );
  }

  // Method to check if property can be published
  canBePublished() {
    return (
      this.isApproved() &&
      this.hasImages() &&
      this.description &&
      this.title &&
      this.isOpen() // NEW: Only open properties can be published
    );
  }

  // Method to add image
  addImage(url) {
    if (!this.imageUrls) {
      this.imageUrls = [];
    }
    this.imageUrls.push(url);
  }

  // Method to remove image
  removeImage(url) {
    if (this.imageUrls) {
      this.imageUrls = this.imageUrls.filter((img) => img !== url);
    }
  }

  // Method to add special feature
  addSpecialFeature(feature) {
    if (!this.specialFeatures) {
      this.specialFeatures = [];
    }
    if (!this.specialFeatures.includes(feature)) {
      this.specialFeatures.push(feature);
    }
  }

  // View tracking methods
  incrementViews(sessionId = null, userAgent = null, ipAddress = null) {
    this.views++;
    this.lastViewedAt = new Date();
    this.updatedAt = new Date();

    // Optional: Add to view history for detailed analytics
    if (sessionId || userAgent || ipAddress) {
      this.viewHistory.push({
        timestamp: new Date().toISOString(),
        sessionId: sessionId,
        userAgent: userAgent,
        ipAddress: ipAddress,
      });

      // Keep only last 100 views to prevent unlimited growth
      if (this.viewHistory.length > 100) {
        this.viewHistory = this.viewHistory.slice(-100);
      }
    }
  }

  getViewsCount() {
    return this.views;
  }

  getFormattedViews() {
    if (this.views === 0) return "No views yet";
    if (this.views === 1) return "1 view";
    return `${this.views} views`;
  }

  getRecentViews(days = 7) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return this.viewHistory.filter(
      (view) => new Date(view.timestamp) > cutoffDate
    ).length;
  }

  // Copy method similar to Flutter's copyWith
  copyWith(updates = {}) {
    return new Property({
      // Basic Information
      id: updates.id !== undefined ? updates.id : this.id,
      userName:
        updates.userName !== undefined ? updates.userName : this.userName,
      userEmail:
        updates.userEmail !== undefined ? updates.userEmail : this.userEmail,
      userContact:
        updates.userContact !== undefined
          ? updates.userContact
          : this.userContact,
      adminName:
        updates.adminName !== undefined ? updates.adminName : this.adminName,

      // Admin Approval Status
      status: updates.status !== undefined ? updates.status : this.status,
      statusReason:
        updates.statusReason !== undefined
          ? updates.statusReason
          : this.statusReason,
      approvedBy:
        updates.approvedBy !== undefined ? updates.approvedBy : this.approvedBy,
      approvedAt:
        updates.approvedAt !== undefined ? updates.approvedAt : this.approvedAt,

      // Address Details
      country: updates.country !== undefined ? updates.country : this.country,
      city: updates.city !== undefined ? updates.city : this.city,
      area: updates.area !== undefined ? updates.area : this.area,
      street: updates.street !== undefined ? updates.street : this.street,
      googleMap:
        updates.googleMap !== undefined ? updates.googleMap : this.googleMap,
      landmark:
        updates.landmark !== undefined ? updates.landmark : this.landmark,

      // Property Specifications
      quantity:
        updates.quantity !== undefined ? updates.quantity : this.quantity,
      listingType:
        updates.listingType !== undefined
          ? updates.listingType
          : this.listingType,
      propertyType:
        updates.propertyType !== undefined
          ? updates.propertyType
          : this.propertyType,
      furnishing:
        updates.furnishing !== undefined ? updates.furnishing : this.furnishing,
      propertyStatus:
        updates.propertyStatus !== undefined
          ? updates.propertyStatus
          : this.propertyStatus,

      // NEW: Market Status
      marketStatus:
        updates.marketStatus !== undefined
          ? updates.marketStatus
          : this.marketStatus,

      // Size Details
      builtUpArea:
        updates.builtUpArea !== undefined
          ? updates.builtUpArea
          : this.builtUpArea,
      plotSize:
        updates.plotSize !== undefined ? updates.plotSize : this.plotSize,
      areaUnit:
        updates.areaUnit !== undefined ? updates.areaUnit : this.areaUnit,

      // Room Details
      bedrooms:
        updates.bedrooms !== undefined ? updates.bedrooms : this.bedrooms,
      bathrooms:
        updates.bathrooms !== undefined ? updates.bathrooms : this.bathrooms,
      livingRooms:
        updates.livingRooms !== undefined
          ? updates.livingRooms
          : this.livingRooms,
      kitchens:
        updates.kitchens !== undefined ? updates.kitchens : this.kitchens,
      parkingSpaces:
        updates.parkingSpaces !== undefined
          ? updates.parkingSpaces
          : this.parkingSpaces,

      // Listing Information
      listedBy:
        updates.listedBy !== undefined ? updates.listedBy : this.listedBy,
      ownershipStatus:
        updates.ownershipStatus !== undefined
          ? updates.ownershipStatus
          : this.ownershipStatus,
      mortgageStatus:
        updates.mortgageStatus !== undefined
          ? updates.mortgageStatus
          : this.mortgageStatus,
      exclusivity:
        updates.exclusivity !== undefined
          ? updates.exclusivity
          : this.exclusivity,

      // Pricing Information
      listPrice:
        updates.listPrice !== undefined ? updates.listPrice : this.listPrice,
      finalPrice:
        updates.finalPrice !== undefined ? updates.finalPrice : this.finalPrice,
      currency:
        updates.currency !== undefined ? updates.currency : this.currency,
      priceNegotiable:
        updates.priceNegotiable !== undefined
          ? updates.priceNegotiable
          : this.priceNegotiable,

      // Marketing Preferences
      marketingPreference:
        updates.marketingPreference !== undefined
          ? updates.marketingPreference
          : this.marketingPreference,
      webListingDetails:
        updates.webListingDetails !== undefined
          ? updates.webListingDetails
          : this.webListingDetails,

      // Additional Information
      listingInstructions:
        updates.listingInstructions !== undefined
          ? updates.listingInstructions
          : this.listingInstructions,
      specialFeatures:
        updates.specialFeatures !== undefined
          ? updates.specialFeatures
          : this.specialFeatures,
      otherFeatures:
        updates.otherFeatures !== undefined
          ? updates.otherFeatures
          : this.otherFeatures,

      // Media
      imageUrls:
        updates.imageUrls !== undefined ? updates.imageUrls : this.imageUrls,
      videoUrl:
        updates.videoUrl !== undefined ? updates.videoUrl : this.videoUrl,
      floorPlans:
        updates.floorPlans !== undefined ? updates.floorPlans : this.floorPlans,

      // Description
      description:
        updates.description !== undefined
          ? updates.description
          : this.description,
      title: updates.title !== undefined ? updates.title : this.title,

      // Timestamps
      createdAt:
        updates.createdAt !== undefined ? updates.createdAt : this.createdAt,
      updatedAt:
        updates.updatedAt !== undefined ? updates.updatedAt : this.updatedAt,
      availableFrom:
        updates.availableFrom !== undefined
          ? updates.availableFrom
          : this.availableFrom,

      // View Tracking
      views: updates.views !== undefined ? updates.views : this.views,
      viewHistory:
        updates.viewHistory !== undefined
          ? updates.viewHistory
          : this.viewHistory,
      lastViewedAt:
        updates.lastViewedAt !== undefined
          ? updates.lastViewedAt
          : this.lastViewedAt,
    });
  }
}
